import { UseQueryOptions, useMutation, useQueryClient } from 'react-query'
import { getStore } from 'app/state/storeRegistry'
import {
  addConnections,
  addToUpdatingConnectionsToolbox,
  deleteFromUpdatingConnectionsToolbox,
  setCreateConnectionError,
  setCreateConnectionStatus
} from '../state/connectionsSlice'
import { Connection, DatabricksMetadataPayload } from '../types'
import {
  getBigQueryConnectionProjects,
  getConnections,
  getBqProjects,
  getConnection,
  getConnectionRelatedWorkflows,
  getDatabricksMetadata,
  getExistingDatabricksMetadata,
  getDatabricksWarehouseMetadata,
  getExistingDatabricksWarehouseMetadata,
  getConnectionRelatedMaps,
  saveConnection
} from './connections'
import { queryClient, queryClientUpdateCacheArrayElement } from 'queryClient'
import { accountDataQuery, accountKeys } from 'features/account/api/accountQueries'
import settings from 'settings'
import { useAppDispatch } from 'app/state'
import { CartoApiError, RequestStatus } from 'features/common'
import { getAccountGovernanceConnections } from 'features/account/api/account'

const CONNECTIONS_KEY_ROOT = 'connections'
export const connectionsQueryKeys = {
  all: [CONNECTIONS_KEY_ROOT] as const,
  connections: [CONNECTIONS_KEY_ROOT, 'connections'],
  bqConnectionProjects: [CONNECTIONS_KEY_ROOT, 'connections', 'projects'],
  connection: (id: string) => [CONNECTIONS_KEY_ROOT, 'connection', id],
  relatedMaps: (id: string) => [CONNECTIONS_KEY_ROOT, 'connections', 'maps', id],
  relatedWorkflows: (id: string) => [CONNECTIONS_KEY_ROOT, 'connections', 'workflows', id],
  databricksMetadata: [CONNECTIONS_KEY_ROOT, 'connections', 'databricks', 'metadata'],
  recentResources: [CONNECTIONS_KEY_ROOT, 'recentResources']
}

export const useSaveConnection = () => {
  const dispatch = useAppDispatch()
  return useMutation({
    mutationFn: async (connection: Connection) => {
      dispatch(addToUpdatingConnectionsToolbox(connection.id))
      const savedConnection = await saveConnection(connection)
      dispatch(deleteFromUpdatingConnectionsToolbox(connection.id))
      return savedConnection
    },
    onSuccess: (savedConnection: Connection) => {
      queryClientUpdateCacheArrayElement(connectionsQueryKeys.connections, savedConnection)
      queryClient.setQueryData([connectionsQueryKeys.connection, savedConnection.id], savedConnection)
    },
    onError: (error, connection) => {
      // TODO: it's a temporary solution to avoid a refactor in the whole Connections context
      dispatch(setCreateConnectionStatus(RequestStatus.Rejected))
      dispatch(setCreateConnectionError(error as CartoApiError))
      dispatch(deleteFromUpdatingConnectionsToolbox(connection.id))
    }
  })
}

export const useConnectionsMutationRefetch = () => {
  const queryClient = useQueryClient()
  return {
    invalidateConnections: () => {
      queryClient.invalidateQueries(connectionsQueryKeys.all)
    }
  }
}

export const connectionsQuery: UseQueryOptions<Connection[], Error> = {
  queryKey: connectionsQueryKeys.connections,
  queryFn: async () => {
    const [accountData, connectionSettings] = await Promise.all([
      queryClient.fetchQuery(accountDataQuery),
      queryClient.fetchQuery({
        queryKey: accountKeys.accountGovernanceConnections,
        queryFn: () => getAccountGovernanceConnections()
      })
    ])
    const connections = await getConnections()

    return !settings.cartoDwEnabled || !connectionSettings.cartoDataWarehouse || accountData.hideCartoDatawarehouse
      ? connections.filter((c) => !c.carto_dw)
      : connections
  },
  enabled: false,
  refetchOnWindowFocus: 'always',
  staleTime: Infinity,
  onSuccess: (data) => {
    getStore().dispatch(addConnections(data))
  }
}

export const connectionQuery = (id = ''): UseQueryOptions<Connection | null, Error> => ({
  queryKey: connectionsQueryKeys.connection(id),
  queryFn: () => getConnection(id),
  refetchOnWindowFocus: 'always',
  enabled: !!id,
  retry: false
})

function getBqProjectsQuery(config, search = '') {
  const queryOptions = {
    queryKey: [...connectionsQueryKeys.bqConnectionProjects, search],
    queryFn: () => getBqProjects({ ...config, search }),
    staleTime: 1000,
    retry: false
  }

  return queryOptions
}

function getBqProjectsWithConnectionQuery(connectionId, search = '') {
  const queryOptions = {
    queryKey: [...connectionsQueryKeys.bqConnectionProjects, search],
    queryFn: () => getBigQueryConnectionProjects(connectionId, search),
    staleTime: 10000,
    retry: false
  }

  return queryOptions
}

function getConnectionRelatedMapsQuery(connectionId) {
  const queryOptions = {
    queryKey: connectionsQueryKeys.relatedMaps(connectionId),
    queryFn: () => getConnectionRelatedMaps(connectionId)
  }

  return queryOptions
}

function getConnectionRelatedWorkflowsQuery(connectionId) {
  const queryOptions = {
    queryKey: connectionsQueryKeys.relatedWorkflows(connectionId),
    queryFn: () => getConnectionRelatedWorkflows(connectionId)
  }

  return queryOptions
}

function getDatabricksMetadataQuery({
  isValid,
  payload,
  provider
}: {
  isValid: boolean
  payload: DatabricksMetadataPayload
  provider?: string
}) {
  const { includeCatalogs, includeWarehouses, includeClusters, ...otherPayload } = payload
  const queryName = [includeWarehouses, includeClusters, includeCatalogs, provider]
  const isDatabricksWarehouse = provider === 'databricksWarehouse'
  const queryOptions = {
    queryKey: [...connectionsQueryKeys.databricksMetadata, queryName],
    queryFn: () => {
      if ('id' in payload) {
        return isDatabricksWarehouse
          ? getExistingDatabricksWarehouseMetadata(payload.id, { includeWarehouses, includeClusters, includeCatalogs })
          : getExistingDatabricksMetadata(payload.id, { includeWarehouses, includeClusters, includeCatalogs })
      }
      return isDatabricksWarehouse
        ? getDatabricksWarehouseMetadata({
            host: payload.host,
            includeWarehouses,
            includeClusters,
            includeCatalogs,
            ...otherPayload
          })
        : getDatabricksMetadata({
            host: payload.host,
            includeWarehouses,
            includeClusters,
            includeCatalogs,
            ...otherPayload
          })
    },
    enabled: isValid,
    staleTime: 0,
    retry: 0
  }

  return queryOptions
}

export {
  getBqProjectsQuery,
  getBqProjectsWithConnectionQuery,
  getConnectionRelatedMapsQuery,
  getConnectionRelatedWorkflowsQuery,
  getDatabricksMetadataQuery
}
